import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const OUTCOME_STYLE = {
  'resolved':    { bg: '#EAF3DE', color: '#3B6D11' },
  'escalated':   { bg: '#FAEEDA', color: '#854F0B' },
  'observation': { bg: '#F1EFE8', color: '#5F5E5A' },
  'partial':     { bg: '#FAEEDA', color: '#854F0B' },
  'cancelled':   { bg: '#FCEBEB', color: '#A32D2D' },
}

export default function RemoteSessions() {
  const navigate = useNavigate()
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchSessions() }, [])

  async function fetchSessions() {
    const { data } = await supabase
      .from('remote_sessions')
      .select('*, sites(id, name), profiles:technician_id(full_name)')
      .order('started_at', { ascending: false })
      .limit(50)
    setSessions(data || [])
    setLoading(false)
  }

  if (loading) return <div className="spinner" />

  const lastWeek = sessions.filter(s => daysSince(s.started_at) <= 7)
  const resolveRate = lastWeek.length
    ? Math.round(lastWeek.filter(s => s.outcome === 'resolved').length / lastWeek.length * 100)
    : 0
  const avgDuration = lastWeek.length
    ? Math.round(lastWeek.reduce((a, s) => a + (s.duration_minutes || 0), 0) / lastWeek.length)
    : 0
  const escalated = lastWeek.filter(s => s.outcome === 'escalated').length

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Remote Sessions</div>
          <div className="page-subtitle">12-point protocol log</div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-number">{lastWeek.length}</div><div className="stat-label">Sessions (7d)</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: 'var(--green)' }}>{resolveRate}%</div><div className="stat-label">Resolve rate</div></div>
        <div className="stat-card"><div className="stat-number">{avgDuration}m</div><div className="stat-label">Avg duration</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: 'var(--amber)' }}>{escalated}</div><div className="stat-label">Escalated</div></div>
      </div>

      {sessions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📞</div>
          <h3>No remote sessions yet</h3>
          <p>When the remote team runs a 12-point session, it'll log here.</p>
        </div>
      ) : (
        sessions.map(s => {
          const os = OUTCOME_STYLE[s.outcome] || OUTCOME_STYLE.observation
          return (
            <div
              key={s.id}
              className="card"
              style={{ marginBottom: 8, cursor: 'pointer' }}
              onClick={() => navigate(`/sites/${s.sites?.id || ''}`)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>{s.session_number}</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{s.sites?.name}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {s.profiles?.full_name || s.technician_name || 'Unassigned'}
                    · {formatDate(s.started_at)}
                    {s.duration_minutes ? ` · ${s.duration_minutes}m` : ''}
                  </div>
                  {s.issues_found && s.issues_found.length > 0 && (
                    <div style={{ fontSize: 12, color: 'var(--text)', marginTop: 6 }}>
                      {s.issues_found.join(' · ')}
                    </div>
                  )}
                </div>
                {s.outcome && (
                  <span style={{
                    fontSize: 11, padding: '3px 9px', borderRadius: 10,
                    background: os.bg, color: os.color, fontWeight: 500, flexShrink: 0,
                    textTransform: 'capitalize',
                  }}>
                    {s.outcome}
                  </span>
                )}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

function formatDate(d) {
  if (!d) return '—'
  const date = new Date(d)
  const today = new Date()
  const isToday = date.toDateString() === today.toDateString()
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
  const isYesterday = date.toDateString() === yesterday.toDateString()
  if (isToday) return `Today ${date.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}`
  if (isYesterday) return `Yesterday ${date.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}`
  return date.toLocaleDateString('en-US',{month:'short',day:'numeric'})
}

function daysSince(d) {
  if (!d) return 999
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
}
